import type { SingPitchId } from './clingCantaConfig';
import type { ClingExploreLevel } from './clingExploreEngine';

/** Todas las pistas visibles del piano roll (grilla completa). */
export const CLING_PIANO_ROLL_PITCHES: SingPitchId[] = [
  'Do5',
  'Si4',
  'La4',
  'Sol4',
  'Fa4',
  'Mi4',
  'Re4',
  'Do4',
  'Si3',
  'La3',
];

export const MIN_ACTIVE_PITCHES = 2;

export const DEFAULT_ACTIVE_PITCHES: SingPitchId[] = ['Do5', 'Sol4', 'Mi4', 'Do4'];

export interface ClingNoteSelection {
  activePitches: SingPitchId[];
}

const STORAGE_KEY = 'cling-note-selection';

export function sortPitchesHighToLow(pitches: SingPitchId[]): SingPitchId[] {
  const order = CLING_PIANO_ROLL_PITCHES;
  return order.filter((p) => pitches.includes(p));
}

export function loadClingNoteSelection(): ClingNoteSelection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { activePitches: [...DEFAULT_ACTIVE_PITCHES] };
    const parsed = JSON.parse(raw) as { activePitches?: SingPitchId[] };
    const active = sortPitchesHighToLow(parsed.activePitches ?? []);
    if (active.length < MIN_ACTIVE_PITCHES) return { activePitches: [...DEFAULT_ACTIVE_PITCHES] };
    return { activePitches: active };
  } catch {
    return { activePitches: [...DEFAULT_ACTIVE_PITCHES] };
  }
}

export function saveClingNoteSelection(selection: ClingNoteSelection): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ activePitches: sortPitchesHighToLow(selection.activePitches) })
    );
  } catch {
    /* ignore */
  }
}

export function togglePitchSelection(
  selection: ClingNoteSelection,
  pitchId: SingPitchId
): ClingNoteSelection {
  const active = new Set(selection.activePitches);
  if (active.has(pitchId)) {
    if (active.size <= MIN_ACTIVE_PITCHES) return selection;
    active.delete(pitchId);
  } else {
    active.add(pitchId);
  }
  return { activePitches: sortPitchesHighToLow([...active]) };
}

export function warmupSequence(activePitches: SingPitchId[]): SingPitchId[] {
  return sortPitchesHighToLow(activePitches);
}

export function applyNoteSelectionToLevel(
  level: ClingExploreLevel,
  selection: ClingNoteSelection
): ClingExploreLevel {
  const activeLanes = sortPitchesHighToLow(selection.activePitches);
  const filtered = level.items.filter((it) => activeLanes.includes(it.pitchId));
  const items =
    filtered.length > 0 ? filtered : activeLanes.map((pitchId) => ({ pitchId }));
  return {
    ...level,
    displayLanes: CLING_PIANO_ROLL_PITCHES,
    activeLanes,
    items,
  };
}
